/**
 * Customer Rewards Page - Points, Badges, Vouchers
 *  Coins system with 3D visual elements
 */
import { useState, useEffect, Suspense, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Canvas, useFrame } from '@react-three/fiber';
import { Float, RoundedBox, Environment, Text3D, Center } from '@react-three/drei';
import * as THREE from 'three';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { toast } from 'sonner';
import api from '../lib/api';
import {
  ArrowLeft,
  Star,
  Coins,
  Flame,
  Gift,
  Ticket,
  Share2,
  Copy,
  Medal,
  ShoppingCart,
  Package,
  Truck,
  TrendingUp,
  ChevronRight,
  Sparkles,
  Clock
} from 'lucide-react';

// Jamaican Color Palette
const COLORS = {
  green: '#00A86B',
  yellow: '#FFD300',
  black: '#000000',
  white: '#FFFFFF',
};

// ============== 3D COMPONENTS ==============

// 3D  Coin
function Coin3D({ position, scale = 1 }) {
  const meshRef = useRef();
  
  useFrame((state) => {
    if (meshRef.current) {
      meshRef.current.rotation.y += 0.02;
    }
  });

  return (
    <Float speed={2} rotationIntensity={0.1} floatIntensity={0.4}>
      <group ref={meshRef} position={position} scale={scale}>
        {/* Coin body */}
        <mesh>
          <cylinderGeometry args={[0.5, 0.5, 0.08, 32]} />
          <meshStandardMaterial 
            color={COLORS.yellow}
            metalness={0.95}
            roughness={0.1}
          />
        </mesh>
        
        {/* Inner ring */}
        <mesh position={[0, 0.05, 0]}>
          <cylinderGeometry args={[0.35, 0.35, 0.02, 32]} />
          <meshStandardMaterial 
            color={COLORS.green}
            metalness={0.8}
            roughness={0.2}
          />
        </mesh>
        
        {/* Chain link emboss */}
        <mesh position={[-0.1, 0.06, 0]} rotation={[Math.PI / 2, 0, 0]}>
          <torusGeometry args={[0.1, 0.03, 16, 32]} />
          <meshStandardMaterial color={COLORS.yellow} metalness={0.9} roughness={0.1} />
        </mesh>
        <mesh position={[0.1, 0.06, 0]} rotation={[Math.PI / 2, 0, 0]}>
          <torusGeometry args={[0.1, 0.03, 16, 32]} />
          <meshStandardMaterial color={COLORS.yellow} metalness={0.9} roughness={0.1} />
        </mesh>
      </group>
    </Float>
  );
}

// 3D Gift Box
function GiftBox3D({ position }) {
  const meshRef = useRef();
  
  useFrame((state) => {
    if (meshRef.current) {
      meshRef.current.rotation.y = Math.sin(state.clock.elapsedTime) * 0.2;
    }
  });

  return (
    <Float speed={1.5} rotationIntensity={0.2} floatIntensity={0.3}>
      <group ref={meshRef} position={position}>
        {/* Box */}
        <RoundedBox args={[0.6, 0.5, 0.6]} radius={0.05}>
          <meshStandardMaterial color={COLORS.green} metalness={0.3} roughness={0.5} />
        </RoundedBox>
        
        {/* Ribbon horizontal */}
        <mesh position={[0, 0, 0]}>
          <boxGeometry args={[0.65, 0.1, 0.1]} />
          <meshStandardMaterial color={COLORS.yellow} />
        </mesh>
        
        {/* Ribbon vertical */}
        <mesh position={[0, 0, 0]}>
          <boxGeometry args={[0.1, 0.55, 0.1]} />
          <meshStandardMaterial color={COLORS.yellow} />
        </mesh>
        
        {/* Bow */}
        <mesh position={[0, 0.35, 0]}>
          <sphereGeometry args={[0.12, 16, 16]} />
          <meshStandardMaterial color={COLORS.yellow} />
        </mesh>
      </group>
    </Float>
  );
}

// Hero 3D Scene for Customer
function CustomerHeroScene({ points }) {
  return (
    <>
      <ambientLight intensity={0.4} />
      <directionalLight position={[5, 10, 5]} intensity={1} />
      <pointLight position={[-3, 3, 2]} intensity={0.5} color={COLORS.green} />
      <pointLight position={[3, 3, 2]} intensity={0.5} color={COLORS.yellow} />
      
      {/* Central Coin */}
      <Coin3D position={[0, 0, 0]} scale={1.2} />
      
      {/* Orbiting elements */}
      <Coin3D position={[-1.5, 0.3, 0.5]} scale={0.5} />
      <Coin3D position={[1.5, -0.2, 0.5]} scale={0.5} />
      <GiftBox3D position={[-1, -0.5, 0]} />
      
      <Environment preset="city" />
    </>
  );
}

// ============== UI COMPONENTS ==============

// Points Display Card
function PointsCard({ available, total }) {
  return (
    <Card className="bg-gradient-to-br from-yellow-500/20 to-amber-500/20 border-yellow-500/30 p-6">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm text-yellow-300 flex items-center gap-2">
            <Coins className="w-4 h-4" />
             Coins
          </p>
          <p className="text-4xl font-bold text-white mt-1">
            {available?.toLocaleString() || 0}
          </p>
          <p className="text-sm text-slate-400">
            {total?.toLocaleString() || 0} lifetime earned
          </p>
        </div>
        <div className="w-16 h-16 rounded-full bg-gradient-to-br from-yellow-400 to-amber-500 flex items-center justify-center shadow-lg shadow-yellow-500/30">
          <Coins className="w-8 h-8 text-black" />
        </div>
      </div>
    </Card>
  );
}

// Redeem Options
function RedeemSection({ points, onRedeem }) {
  const [redeeming, setRedeeming] = useState(false);
  
  const handleRedeem = async (type, amount) => {
    setRedeeming(true);
    try {
      await onRedeem(type, amount);
    } finally {
      setRedeeming(false);
    }
  };

  return (
    <Card className="bg-slate-800/50 border-slate-700 p-4">
      <h3 className="font-semibold text-white mb-4 flex items-center gap-2">
        <Gift className="w-5 h-5 text-green-400" />
        Redeem Your Coins
      </h3>
      
      <div className="grid md:grid-cols-2 gap-4">
        {/* Voucher Option */}
        <div className="p-4 bg-slate-900/50 rounded-lg border border-slate-700">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-10 h-10 rounded-full bg-green-500/20 flex items-center justify-center">
              <Ticket className="w-5 h-5 text-green-400" />
            </div>
            <div>
              <p className="font-medium text-white">Shop Voucher</p>
              <p className="text-xs text-slate-400">10 coins = J$1</p>
            </div>
          </div>
          
          <div className="flex gap-2">
            {[100, 500, 1000].map(amt => (
              <Button
                key={amt}
                size="sm"
                variant="outline"
                disabled={points < amt || redeeming}
                onClick={() => handleRedeem('voucher', amt)}
                className="flex-1 border-green-500/30 text-green-400 hover:bg-green-500/10"
              >
                {amt} → J${amt / 10}
              </Button>
            ))}
          </div>
        </div>
        
        {/* Free Delivery Option */}
        <div className="p-4 bg-slate-900/50 rounded-lg border border-slate-700">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-10 h-10 rounded-full bg-yellow-500/20 flex items-center justify-center">
              <Truck className="w-5 h-5 text-yellow-400" />
            </div>
            <div>
              <p className="font-medium text-white">Free Delivery</p>
              <p className="text-xs text-slate-400">500 coins = 1 free delivery</p>
            </div>
          </div>
          
          <Button
            size="sm"
            disabled={points < 500 || redeeming}
            onClick={() => handleRedeem('free_delivery', 500)}
            className="w-full bg-yellow-500 hover:bg-yellow-600 text-black"
          >
            Redeem Free Delivery (500 coins)
          </Button>
        </div>
      </div>
    </Card>
  );
}

// Streak Display
function StreakDisplay({ days }) {
  const isActive = days > 0;
  
  return (
    <Card className={`p-4 ${isActive ? 'bg-gradient-to-br from-orange-500/20 to-red-500/20 border-orange-500/30' : 'bg-slate-800/50 border-slate-700'}`}>
      <div className="flex items-center gap-4">
        <div className={`w-12 h-12 rounded-full flex items-center justify-center ${isActive ? 'bg-gradient-to-br from-orange-400 to-red-500' : 'bg-slate-700'}`}>
          <Flame className={`w-6 h-6 ${isActive ? 'text-white' : 'text-slate-500'}`} />
        </div>
        <div>
          <p className="text-xl font-bold text-white">{days} Day Streak</p>
          <p className="text-sm text-slate-400">
            {isActive ? '🔥 Keep it up!' : 'Make a purchase or send a package to start!'}
          </p>
        </div>
        {days >= 7 && (
          <Badge className="bg-green-500/20 text-green-400 border-green-500/30 ml-auto">
            Free Delivery Unlocked!
          </Badge>
        )}
      </div>
    </Card>
  );
}

// Badge Card
function BadgeCard({ badge }) {
  const tierColors = {
    bronze: 'from-amber-700 to-amber-900',
    silver: 'from-slate-400 to-slate-600',
    gold: 'from-yellow-400 to-amber-500',
    platinum: 'from-purple-400 to-indigo-500'
  };
  
  return (
    <div className={`relative p-4 rounded-xl border ${badge.earned ? 'bg-slate-800/80 border-green-500/50' : 'bg-slate-900/50 border-slate-700/50 opacity-60'}`}>
      {badge.earned && (
        <div className="absolute -top-2 -right-2 w-6 h-6 rounded-full bg-green-500 flex items-center justify-center">
          <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
          </svg>
        </div>
      )}
      
      <div className={`w-12 h-12 rounded-full bg-gradient-to-br ${tierColors[badge.tier] || tierColors.bronze} flex items-center justify-center mb-3 shadow-lg`}>
        <span className="text-2xl">{badge.icon}</span>
      </div>
      
      <h4 className="font-semibold text-white mb-1">{badge.name}</h4>
      <p className="text-xs text-slate-400 mb-2">{badge.description}</p>
      
      <div className="flex gap-1">
        {badge.xp_reward > 0 && (
          <Badge className="bg-yellow-500/20 text-yellow-400 border-yellow-500/30 text-xs">
            +{badge.xp_reward} XP
          </Badge>
        )}
        {badge.points_reward > 0 && (
          <Badge className="bg-green-500/20 text-green-400 border-green-500/30 text-xs">
            +{badge.points_reward} pts
          </Badge>
        )}
      </div>
    </div>
  );
}

// Ways to Earn Points
function EarnPointsSection() {
  const earnMethods = [
    { icon: <Package className="w-5 h-5" />, action: 'Send a package', points: 50 },
    { icon: <ShoppingCart className="w-5 h-5" />, action: 'Shop (per J$100)', points: 1 },
    { icon: <Star className="w-5 h-5" />, action: 'Rate a driver', points: 5 },
    { icon: <Share2 className="w-5 h-5" />, action: 'Refer a friend', points: 300 },
  ];

  return (
    <Card className="bg-slate-800/50 border-slate-700 p-4">
      <h3 className="font-semibold text-white mb-4 flex items-center gap-2">
        <Sparkles className="w-5 h-5 text-yellow-400" />
        Ways to Earn Coins
      </h3>
      
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {earnMethods.map((method, i) => (
          <div key={i} className="text-center p-3 bg-slate-900/50 rounded-lg">
            <div className="w-10 h-10 rounded-full bg-green-500/20 flex items-center justify-center mx-auto mb-2 text-green-400">
              {method.icon}
            </div>
            <p className="text-xs text-slate-400">{method.action}</p>
            <p className="font-bold text-yellow-400">+{method.points}</p>
          </div>
        ))}
      </div>
    </Card>
  );
}

// ============== MAIN COMPONENT ==============

export default function CustomerRewards() {
  const navigate = useNavigate();
  const { user } = useAuth();
  
  const [state, setState] = useState(null);
  const [badges, setBadges] = useState([]);
  const [loading, setLoading] = useState(true);
  const [referralCode, setReferralCode] = useState('');

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [stateRes, badgesRes, referralRes] = await Promise.all([
        api.get('/gamification/my-state?role=customer'),
        api.get('/gamification/my-badges?role=customer'),
        api.get('/gamification/referral-code?role=customer')
      ]);
      
      setState(stateRes.data);
      setBadges(badgesRes.data.badges || []);
      setReferralCode(referralRes.data.referral_code || '');
    } catch (error) {
      console.error('Error fetching gamification data:', error);
      toast.error('Failed to load rewards data');
    } finally {
      setLoading(false);
    }
  };

  const handleRedeem = async (type, points) => {
    try {
      const res = await api.post(`/gamification/redeem-points?points=${points}&redemption_type=${type}&role=customer`);
      
      if (res.data.success) {
        toast.success(
          type === 'voucher' 
            ? `J$${res.data.voucher_value} voucher added!` 
            : `${res.data.free_deliveries} free delivery added!`
        );
        fetchData(); // Refresh data
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Redemption failed');
    }
  };

  const copyReferralCode = () => {
    navigator.clipboard.writeText(referralCode);
    toast.success('Referral code copied!');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-yellow-500 border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-slate-400">Loading rewards...</p>
        </div>
      </div>
    );
  }

  const earnedBadges = badges.filter(b => b.earned);
  const unearnedBadges = badges.filter(b => !b.earned);

  return (
    <div className="min-h-screen bg-slate-950 text-white">
      {/* Header */}
      <header className="bg-slate-900 border-b border-slate-800 sticky top-0 z-50">
        <div className="max-w-6xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button 
                variant="ghost" 
                size="icon"
                onClick={() => navigate('/dashboard')}
                className="text-slate-400 hover:text-white"
              >
                <ArrowLeft className="w-5 h-5" />
              </Button>
              <div>
                <h1 className="text-lg font-bold flex items-center gap-2">
                  <Coins className="w-5 h-5 text-yellow-500" />
                   Rewards
                </h1>
                <p className="text-sm text-slate-400">
                  Level {state?.level || 1}
                </p>
              </div>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 py-6 space-y-6">
        {/* 3D Hero Section */}
        <div className="grid md:grid-cols-2 gap-6">
          {/* 3D Scene */}
          <Card className="bg-gradient-to-br from-slate-800 to-slate-900 border-slate-700 h-64 overflow-hidden">
            <Suspense fallback={
              <div className="h-full flex items-center justify-center">
                <div className="animate-spin w-8 h-8 border-2 border-yellow-500 border-t-transparent rounded-full" />
              </div>
            }>
              <Canvas camera={{ position: [0, 0, 4], fov: 50 }}>
                <CustomerHeroScene points={state?.available_points || 0} />
              </Canvas>
            </Suspense>
          </Card>
          
          {/* Points Card */}
          <div className="space-y-4">
            <PointsCard 
              available={state?.available_points || 0}
              total={state?.total_points || 0}
            />
            
            {/* Stats */}
            <div className="grid grid-cols-3 gap-3">
              <div className="text-center p-3 bg-slate-800/50 rounded-lg border border-slate-700">
                <p className="text-xl font-bold text-green-400">{state?.total_packages_sent || 0}</p>
                <p className="text-xs text-slate-400">Packages Sent</p>
              </div>
              <div className="text-center p-3 bg-slate-800/50 rounded-lg border border-slate-700">
                <p className="text-xl font-bold text-yellow-400">{state?.total_purchases || 0}</p>
                <p className="text-xs text-slate-400">Purchases</p>
              </div>
              <div className="text-center p-3 bg-slate-800/50 rounded-lg border border-slate-700">
                <p className="text-xl font-bold text-purple-400">{earnedBadges.length}</p>
                <p className="text-xs text-slate-400">Badges</p>
              </div>
            </div>
          </div>
        </div>

        {/* Streak */}
        <StreakDisplay days={state?.streak_days || 0} />

        {/* Available Benefits */}
        {(state?.free_deliveries > 0 || state?.voucher_balance > 0) && (
          <Card className="bg-gradient-to-r from-green-500/10 to-emerald-500/10 border-green-500/30 p-4">
            <h3 className="font-semibold text-white mb-3 flex items-center gap-2">
              <Gift className="w-5 h-5 text-green-400" />
              Your Benefits
            </h3>
            <div className="flex gap-4">
              {state?.free_deliveries > 0 && (
                <Badge className="bg-yellow-500/20 text-yellow-400 border-yellow-500/30 px-4 py-2">
                  <Truck className="w-4 h-4 mr-2" />
                  {state.free_deliveries} Free Deliveries
                </Badge>
              )}
              {state?.voucher_balance > 0 && (
                <Badge className="bg-green-500/20 text-green-400 border-green-500/30 px-4 py-2">
                  <Ticket className="w-4 h-4 mr-2" />
                  J${state.voucher_balance.toFixed(0)} Voucher Balance
                </Badge>
              )}
            </div>
          </Card>
        )}

        {/* Earn Points */}
        <EarnPointsSection />

        {/* Redeem Points */}
        <RedeemSection 
          points={state?.available_points || 0}
          onRedeem={handleRedeem}
        />

        {/* Referral Section */}
        <Card className="bg-gradient-to-r from-purple-500/10 to-pink-500/10 border-purple-500/30 p-4">
          <div className="flex items-center justify-between flex-wrap gap-4">
            <div>
              <h3 className="font-semibold text-white flex items-center gap-2">
                <Share2 className="w-5 h-5 text-purple-400" />
                Invite Friends, Earn Rewards
              </h3>
              <p className="text-sm text-slate-400 mt-1">
                Get 300 coins when they make their first order
              </p>
            </div>
            <div className="flex items-center gap-2">
              <code className="px-3 py-2 bg-slate-800 rounded-lg text-purple-400 font-mono">
                {referralCode}
              </code>
              <Button size="sm" variant="outline" onClick={copyReferralCode}>
                <Copy className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </Card>

        {/* Badges Section */}
        <div>
          <h2 className="text-xl font-bold mb-4 flex items-center gap-2">
            <Medal className="w-6 h-6 text-yellow-500" />
            Badges
            <span className="text-sm text-slate-400 font-normal">
              ({earnedBadges.length}/{badges.length} earned)
            </span>
          </h2>
          
          {/* Earned Badges */}
          {earnedBadges.length > 0 && (
            <div className="mb-6">
              <h3 className="text-sm text-green-400 mb-3 font-medium">🏆 Earned</h3>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {earnedBadges.map(badge => (
                  <BadgeCard key={badge.id} badge={badge} />
                ))}
              </div>
            </div>
          )}
          
          {/* Locked Badges */}
          {unearnedBadges.length > 0 && (
            <div>
              <h3 className="text-sm text-slate-400 mb-3 font-medium">🔒 Locked</h3>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {unearnedBadges.map(badge => (
                  <BadgeCard key={badge.id} badge={badge} />
                ))}
              </div>
            </div>
          )}
        </div>
      </main>
    </div>
  );
}
